import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const BRAND_NAVY = "#002FA7";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
          <Link to="/landing" className="font-serif text-xl font-bold tracking-tight" style={{ color: BRAND_NAVY }}>
            BilloraLaw
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-serif font-bold mb-2" style={{ color: BRAND_NAVY }}>
          Política de Privacidad
        </h1>
        <p className="text-sm text-slate-400 mb-10">Última actualización: Mayo 2026</p>

        <div className="space-y-6 text-sm text-slate-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-serif [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:list-[lower-alpha] [&_ol]:pl-6 [&_ol]:space-y-1 [&_hr]:my-8 [&_hr]:border-slate-200 [&_p]:mb-3 [&_strong]:text-slate-900 [&_a]:text-[#002FA7] [&_a]:underline">

          <p>
            Esta Política de Privacidad establece los términos en que Billora Law ("nosotros", "nuestro" o "la empresa") usa y protege la información que es proporcionada por sus usuarios al momento de utilizar nuestro sitio web billoralaw.com (el "Sitio"). Nuestra empresa está comprometida con la seguridad de los datos de sus usuarios.
          </p>
          <p>
            Cuando le pedimos llenar campos de información personal con la cual usted pueda ser identificado, lo hacemos asegurando que sólo se empleará de acuerdo con los términos de este documento. Esta Política de Privacidad puede cambiar con el tiempo o ser actualizada, por lo que le recomendamos revisar continuamente esta página para asegurarse de que está de acuerdo con dichos cambios.
          </p>

          <hr />

          <h2>1. Introducción y Compromiso</h2>
          <p>
            Billora Law cumple con la Ley 19.628 sobre Protección de Datos Personales y sus modificaciones posteriores, en particular la reforma introducida por la Ley 21.096 que modernizó la regulación de privacidad en Chile.
          </p>
          <p>Cuando usted proporciona información personal a través de nuestro Sitio, confiamos en que esa información será tratada con cuidado y seguridad. Nos comprometemos a:</p>
          <ul>
            <li>Usar tu información solo para los fines descritos en esta política</li>
            <li>Proteger tu información con medidas de seguridad robustas</li>
            <li>Ser transparente sobre cómo la usamos</li>
            <li>Respetar tus derechos como titular de datos</li>
          </ul>

          <hr />

          <h2>2. Información que Recopilamos</h2>
          <p>Billora Law recopila información de tres tipos de titulares de datos:</p>
          <ol type="a">
            <li>Clientes empresariales de la firma de abogados</li>
            <li>Representantes y contactos de clientes empresariales</li>
            <li>Profesionales y personal de la firma de abogados que registran tiempo y actividades</li>
          </ol>

          <h3>2.1 Datos de clientes empresariales</h3>
          <p>Cuando una empresa cliente se registra en Billora, recopilamos:</p>
          <ul>
            <li>Razón social de la empresa</li>
            <li>RUT de la empresa</li>
            <li>Dirección legal / domicilio</li>
            <li>Correo electrónico de contacto</li>
            <li>Número de teléfono</li>
            <li>Sitio web (si aplica)</li>
            <li>Información de proyectos y actividades asociados a esa empresa</li>
          </ul>

          <h3>2.2 Datos de representantes y contactos de clientes</h3>
          <p>Para cada representante o contacto de la empresa cliente, recopilamos:</p>
          <ul>
            <li>Nombre completo</li>
            <li>RUT (opcional, dependiendo de la naturaleza del contacto)</li>
            <li>Cargo o rol dentro de la empresa</li>
            <li>Dirección de contacto</li>
            <li>Correo electrónico</li>
            <li>Número de teléfono</li>
          </ul>

          <h3>2.3 Datos de profesionales y personal de la firma de abogados</h3>
          <p>Cuando los abogados, profesionales y personal de la firma registran su actividad en Billora, recopilamos:</p>
          <ul>
            <li>Nombre completo</li>
            <li>Cargo / rol profesional (abogado, contador, asistente, etc.)</li>
            <li>Número de horas trabajadas (diarias, semanales, por proyecto)</li>
            <li>Actividades realizadas (descripciones de trabajo)</li>
            <li>Proyectos y empresas-cliente asociados a cada profesional</li>
            <li>Información de tiempo y asistencia</li>
          </ul>

          <h3>2.4 Datos de proyectos y actividades</h3>
          <p>Para cada proyecto o actividad registrado en Billora, almacenamos:</p>
          <ul>
            <li>Nombre del proyecto</li>
            <li>Empresa-cliente asociado</li>
            <li>Profesionales asignados</li>
            <li>Descripción de actividades</li>
            <li>Horas trabajadas (por profesional)</li>
            <li>Montos facturables (expresados en UF y pesos chilenos)</li>
            <li>Fechas de inicio y término</li>
            <li>Estado del proyecto</li>
          </ul>

          <h3>2.5 Datos financieros y de facturación</h3>
          <p>Billora procesa y almacena:</p>
          <ul>
            <li>Montos facturables en UF (Unidad de Fomento)</li>
            <li>Montos facturables en pesos chilenos</li>
            <li>Información de tasas horarias por profesional</li>
            <li>Registros de tiempo para generación de facturas</li>
            <li>Información de pago (procesada a través de terceros seguros)</li>
          </ul>

          <h3>2.6 Información recopilada automáticamente</h3>
          <p>Cuando navegas por el Sitio o utilizas la plataforma Billora, recopilamos automáticamente:</p>
          <ul>
            <li>Dirección IP</li>
            <li>Tipo de navegador y sistema operativo</li>
            <li>Páginas visitadas y tiempo de permanencia</li>
            <li>Fuente de referencia</li>
            <li>Cookies y tecnologías similares</li>
            <li>Datos de interacción con formularios</li>
            <li>Información de dispositivo</li>
            <li>Logs de acceso a la plataforma</li>
          </ul>

          <h3>2.7 Información de terceros</h3>
          <p>Podemos recibir información de:</p>
          <ul>
            <li>Plataformas de análisis (Google Analytics)</li>
            <li>Proveedores de correo electrónico</li>
            <li>Integraciones con servicios de calendario o CRM</li>
            <li>Referencias de otros usuarios o clientes</li>
          </ul>

          <hr />

          <h2>3. Base Legal para el Tratamiento y Roles de Responsabilidad</h2>

          <h3>3.1 Quién es responsable de tus datos</h3>
          <p>En Billora Law, los roles de tratamiento de datos funcionan así:</p>
          <p><strong>Para clientes empresariales:</strong> La firma de abogados es "Responsable" de los datos de sus propios clientes empresariales. Billora Law actúa como "Encargado de Tratamiento" en nombre de la firma.</p>
          <p><strong>Para profesionales y personal de la firma:</strong> La firma de abogados es "Responsable" de datos de sus empleados. Billora Law actúa como "Encargado de Tratamiento".</p>
          <p><strong>Para representantes y contactos de clientes:</strong> La firma de abogados es "Responsable". Billora Law es "Encargado de Tratamiento".</p>
          <p>En la práctica, esto significa que:</p>
          <ul>
            <li>La firma de abogados es responsable ante autoridades y titulares de datos por cómo se usan esos datos</li>
            <li>Billora Law es responsable de proteger esos datos conforme a lo que la firma instruya</li>
            <li>Ambas partes están obligadas a cumplir con Ley 19.628</li>
          </ul>

          <h3>3.2 Base legal para el tratamiento</h3>
          <p>Tratamos tus datos personales sobre las siguientes bases legales:</p>
          <ul>
            <li>Consentimiento explícito que has proporcionado a través de la firma de abogados</li>
            <li>Cumplimiento de contrato o relación comercial con la firma</li>
            <li>Interés legítimo de la firma en prestar servicios y mejorar eficiencia operacional</li>
            <li>Obligaciones legales aplicables en Chile (SII, DT, Tribunal, registros contables, etc.)</li>
          </ul>

          <h3>3.3 Cómo obtenemos consentimiento</h3>
          <p>Cuando es necesario, el consentimiento informado se obtiene a través de:</p>
          <ul>
            <li>Aceptación de términos al registrarse en Billora</li>
            <li>Consentimiento previo de la firma de abogados</li>
            <li>Solicitud directa de servicios</li>
            <li>Comunicaciones específicas sobre tratamiento de datos</li>
          </ul>

          <h3>3.4 Derecho a retirar consentimiento</h3>
          <p>En cualquier momento puedes retirar tu consentimiento escribiendo a nuestro correo de privacidad, sin que afecte tratamientos anteriores lícitamente realizados. Sin embargo, si eres empleado o contacto registrado de una firma, la firma puede requerir ciertos datos por obligaciones legales (SII, DT, etc.).</p>

          <hr />

          <h2>4. Uso de la Información Recogida</h2>
          <p>Los datos recopilados en Billora Law se utilizan específicamente para:</p>

          <h3>4.1 Prestación de servicios de billing y gestión de proyectos</h3>
          <ul>
            <li>Registrar y calcular horas trabajadas por cada profesional</li>
            <li>Asociar horas a proyectos específicos y clientes</li>
            <li>Generar facturas precisas basadas en tiempo trabajado</li>
            <li>Calcular montos facturables en UF y pesos chilenos</li>
            <li>Administrar proyectos y actividades de la firma</li>
            <li>Mantener registros de clientes empresariales y sus contactos</li>
            <li>Procesar pagos y pagos recibidos</li>
          </ul>

          <h3>4.2 Administración y operación de la plataforma</h3>
          <ul>
            <li>Mantener la funcionalidad técnica de Billora</li>
            <li>Crear y administrar cuentas de usuarios</li>
            <li>Gestionar accesos y permisos según rol en la firma</li>
            <li>Diagnóstico y resolución de problemas técnicos</li>
            <li>Respaldo y recuperación de datos</li>
          </ul>

          <h3>4.3 Análisis y mejora de servicios</h3>
          <ul>
            <li>Analizar uso de la plataforma para optimizar funcionalidades</li>
            <li>Generar reportes de desempeño y productividad para la firma</li>
            <li>Mejorar características basadas en patrones de uso</li>
            <li>Realizar pruebas de funcionalidad</li>
          </ul>

          <h3>4.4 Comunicaciones</h3>
          <ul>
            <li>Notificaciones sobre cambios en proyectos o actividades</li>
            <li>Confirmaciones de facturación</li>
            <li>Avisos sobre actualizaciones o mantenimiento</li>
            <li>Respuestas a consultas de soporte</li>
          </ul>

          <h3>4.5 Cumplimiento legal y obligaciones regulatorias</h3>
          <ul>
            <li>Cumplir con obligaciones del Servicio de Impuestos Internos (SII)</li>
            <li>Mantener registros según obligaciones contables en Chile</li>
            <li>Responder a requerimientos de autoridades</li>
            <li>Auditoría y compliance</li>
            <li>Proteger derechos de la firma y de terceros</li>
          </ul>

          <h3>4.6 Seguridad y prevención de fraude</h3>
          <ul>
            <li>Detectar y prevenir uso no autorizado</li>
            <li>Proteger contra acceso malicioso</li>
            <li>Mantener registros de seguridad</li>
          </ul>

          <hr />

          <h2>5. Cookies y Tecnologías de Seguimiento</h2>
          <p>Una cookie se refiere a un fichero que es enviado con la finalidad de solicitar permiso para almacenarse en tu ordenador. Al aceptar dicho fichero se crea y la cookie sirve entonces para tener información respecto al tráfico web y facilita las futuras visitas a una web recurrente.</p>

          <h3>5.1 Uso de cookies</h3>
          <ul>
            <li>Recordar tus preferencias</li>
            <li>Autenticar tu sesión</li>
            <li>Rastrear análisis y comportamiento en el sitio (Google Analytics)</li>
            <li>Mejorar funcionalidades</li>
          </ul>

          <h3>5.2 Tipos de cookies</h3>
          <ul>
            <li><strong>Cookies esenciales:</strong> necesarias para funcionamiento del sitio</li>
            <li><strong>Cookies de análisis:</strong> rastrean cómo usas el Sitio</li>
            <li><strong>Cookies de marketing:</strong> para campañas y retargeting</li>
            <li><strong>Cookies de terceros:</strong> herramientas integradas</li>
          </ul>

          <h3>5.3 Control de cookies</h3>
          <p>Puedes controlar cookies a través de configuración de tu navegador, herramienta de consentimiento en el Sitio, o Do Not Track (DNT) si lo configuras. Puedes eliminar las cookies en cualquier momento desde tu ordenador. Sin embargo, las cookies ayudan a proporcionar un mejor servicio; desactivarlas puede afectar algunas funcionalidades del Sitio.</p>

          <hr />

          <h2>6. Información Compartida con Terceros</h2>
          <p>Billora Law no venderá, cederá ni distribuirá la información personal sin consentimiento, salvo por requisitos legales o instrucciones de la firma de abogados.</p>

          <h3>6.1 Proveedores de servicios esenciales</h3>
          <p>Compartimos datos únicamente con terceros que nos ayudan a operar Billora:</p>
          <ul>
            <li>Proveedores de almacenamiento en la nube (AWS, Google Cloud)</li>
            <li>Procesadores de pago (Stripe, PayU, similar)</li>
            <li>Proveedores de análisis técnico (Google Analytics - datos agregados)</li>
            <li>Herramientas de seguridad y backup</li>
          </ul>
          <p>Todos los proveedores están sujetos a acuerdos de confidencialidad y protección de datos conforme a Ley 19.628. No tienen autorización para usar tu información para sus propios fines comerciales.</p>

          <h3>6.2 Acceso restringido dentro de la firma</h3>
          <p>Los datos en Billora son accesibles solo a administradores de la firma autorizados, profesionales con asignación a proyectos específicos, y personal administrativo con permisos configurados. Billora permite a la firma establecer diferentes niveles de acceso según rol.</p>

          <h3>6.3 Requisitos legales y autoridades</h3>
          <p>Podemos compartir datos cuando lo ordena un tribunal competente, autoridades públicas lo requieren legalmente, organismos fiscales (SII) lo solicitan conforme a obligaciones tributarias, instituciones regulatorias lo requieren, o para defensa de derechos en procesos judiciales. En estos casos, notificaremos a la firma cuando sea legalmente permitido.</p>

          <h3>6.4 Transferencias de negocio</h3>
          <p>En caso de fusión, adquisición o venta de activos de Billora Law, los datos personales pueden ser transferidos al nuevo titular. Se notificará con anticipación a las firmas clientes y se requerirá consentimiento para cambios materiales en tratamiento.</p>

          <h3>6.5 No compartir con fines comerciales</h3>
          <p>Billora no comparte datos con terceros para marketing o publicidad dirigida, venta de datos a otros proveedores, creación de perfiles de consumidor, ni fines comerciales que no sean la prestación del servicio.</p>

          <h3>6.6 Datos entre usuarios de la plataforma</h3>
          <p>Si tu firma usa Billora, otros usuarios autorizados en tu firma pueden acceder a datos que compartan contigo (proyectos, horas, montos). Este acceso está controlado por la firma mediante configuración de permisos.</p>

          <hr />

          <h2>7. Protección de Datos Sensibles y Secreto Profesional</h2>

          <h3>7.1 Datos de naturaleza sensible</h3>
          <p>Billora Law puede procesar datos de naturaleza sensible en el contexto de actividades legales, tales como información relativa a casos legales o litigios, datos de clientes de la firma, información financiera de clientes, y descripciones de proyectos que pueden revelar información confidencial.</p>

          <h3>7.2 Cumplimiento de secreto profesional</h3>
          <p>La firma de abogados es titular del secreto profesional respecto a clientes. Billora:</p>
          <ul>
            <li>No accede al contenido detallado de expedientes o comunicaciones confidenciales</li>
            <li>Procesa únicamente datos administrativos necesarios para facturación y gestión de proyectos</li>
            <li>No almacena argumentos legales, estrategias o comunicaciones privilegiadas</li>
            <li>Confía en que la firma segrega información confidencial de información administrativa</li>
          </ul>

          <h3>7.3 Responsabilidad de la firma</h3>
          <p>La firma de abogados es responsable de determinar qué información ingresa en Billora, segregar información sujeta a secreto profesional, instruir a su personal sobre confidencialidad, y verificar que la información registrada no vulnere derechos de terceros.</p>

          <h3>7.4 Medidas especiales de seguridad</h3>
          <p>Para proteger datos sensibles, Billora implementa encriptación de datos en tránsito y en reposo, control de acceso basado en roles, auditoría de accesos, logs detallados, y segregación de datos por firma cliente.</p>

          <h3>7.5 Indicaciones para el uso responsable</h3>
          <p>Las firmas que usan Billora deben: no registrar documentos legales completos, no ingresar comunicaciones confidenciales con clientes, usar Billora solo para tiempo, proyectos y facturación, mantener expedientes confidenciales en sistemas separados, y entrenar al personal sobre el uso apropiado de la plataforma.</p>

          <hr />

          <h2>8. Integraciones con Servicios Externos</h2>
          <p>Si autorizas la integración de tu cuenta con servicios externos, aquí describimos cómo los usamos:</p>

          <h3>8.1 Google Calendar (si aplica)</h3>
          <p>Cuando autorizas la integración de tu cuenta de Google con la plataforma Billora para agendar reuniones:</p>
          <p><strong>Datos que recopilamos:</strong> Permiso para crear, editar y eliminar eventos en tus calendarios. Información básica de la cuenta de Google (correo y nombre de usuario) únicamente para establecer la conexión segura.</p>
          <p><strong>Importante:</strong> Billora no lee, muestra ni almacena eventos existentes del calendario en la plataforma. Tampoco accede a calendarios ni eventos ajenos a los creados desde Billora.</p>
          <p><strong>Cómo usamos los datos:</strong> Crear automáticamente una cita de calendario con información registrada en Billora. Modificar o eliminar estos eventos si fueron creados desde Billora.</p>
          <p><strong>Protección:</strong> Empleamos autenticación segura OAuth 2.0. Toda transmisión de datos se realiza mediante canales cifrados (HTTPS). Solo accedemos a permisos mínimos necesarios. Los tokens de acceso no se almacenan sin cifrado.</p>
          <p><strong>Revocación:</strong> Puedes revisar, modificar o revocar el acceso desde tu cuenta de Google en: <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">https://myaccount.google.com/permissions</a>. Si revocas el permiso, el vínculo se invalida automáticamente.</p>

          <h3>8.2 Otros servicios</h3>
          <p>Si en el futuro integramos otros servicios (Zapier, Slack, Microsoft Teams, etc.), publicaremos secciones adicionales describiendo cómo se protegen tus datos.</p>

          <hr />

          <h2>9. Retención de Datos</h2>
          <p>La retención de datos en Billora varía según el tipo de titular y legislación aplicable:</p>

          <h3>9.1 Datos de clientes empresariales</h3>
          <p>Se conservan mientras la firma mantenga relación comercial con Billora. Después de finalizar la relación, se retienen máximo 6 años para fines tributarios (SII). Pasado este plazo, se eliminan o anonimizan salvo obligación legal específica.</p>

          <h3>9.2 Datos de profesionales y empleados de la firma</h3>
          <p>Se conservan mientras trabajen en la firma. Después de la desvinculación, se retienen mínimo 30 años para fines contables (requisito legal) y máximo 6 años para otros propósitos. Datos de horas y facturación se retienen 6 años por obligaciones tributarias.</p>

          <h3>9.3 Datos de representantes y contactos de clientes</h3>
          <p>Se conservan mientras exista relación entre firma y cliente. Se retienen máximo 6 años después de finalizada esa relación.</p>

          <h3>9.4 Procedimiento de eliminación</h3>
          <p>Pasados los plazos, eliminamos activamente datos o los anonimizamos. La firma puede solicitar eliminación anticipada cuando sea legalmente permitido. Billora mantiene respaldos según políticas de seguridad y recuperación de desastres.</p>

          <h3>9.5 Derecho a solicitar eliminación</h3>
          <p>Si consideras que tus datos deberían ser eliminados antes de los plazos, puedes contactarnos. Evaluaremos la solicitud considerando obligaciones legales de retención, necesidad del dato para servicios en curso, y derechos de otros titulares de datos.</p>

          <hr />

          <h2>10. Seguridad de Datos</h2>

          <h3>10.1 Medidas de protección</h3>
          <p>Implementamos medidas técnicas, administrativas y físicas para proteger tu información:</p>
          <ul>
            <li>Encriptación SSL/TLS en toda comunicación</li>
            <li>Encriptación de datos en reposo</li>
            <li>Bases de datos protegidas con contraseñas sólidas y autenticación multifactor</li>
            <li>Acceso restringido a personal autorizado</li>
            <li>Firewalls y sistemas de detección de intrusiones</li>
            <li>Auditorías de seguridad periódicas</li>
            <li>Respaldo automático y redundancia</li>
            <li>Segregación de datos por cliente</li>
            <li>Logs de acceso y auditoría de actividades</li>
          </ul>

          <h3>10.2 Control de acceso</h3>
          <ul>
            <li>Autenticación de usuarios con credenciales seguras</li>
            <li>Opciones de autenticación multifactor (2FA)</li>
            <li>Roles y permisos granulares configurables por firma</li>
            <li>Cierre automático de sesiones por inactividad</li>
            <li>Bloqueo de acceso por intentos fallidos</li>
          </ul>

          <h3>10.3 Limitaciones</h3>
          <p>Aunque implementamos medidas robustas, ningún sistema es 100% seguro. No podemos garantizar seguridad absoluta en la transmisión de datos por internet.</p>

          <h3>10.4 Incidentes de seguridad</h3>
          <p>Si ocurre un incidente de seguridad que exponga datos personales: notificaremos a la firma cliente afectada dentro de 24 horas, trabajaremos con autoridades si es requerido, notificaremos a autoridades competentes según ley dentro de 72 horas si es necesario, y proporcionaremos asistencia para mitigar daños.</p>

          <hr />

          <h2>11. Derechos del Titular de Datos</h2>
          <p>Bajo la Ley 19.628, tienes derecho a:</p>

          <h3>Derecho de acceso</h3>
          <p>Solicitar qué datos personales tenemos sobre ti, obtener copia de tus datos en formato legible, y conocer cómo están siendo utilizados.</p>

          <h3>Derecho de rectificación</h3>
          <p>Corregir datos inexactos o incompletos. Solicitar actualización de información.</p>

          <h3>Derecho de eliminación (olvido)</h3>
          <p>Solicitar eliminación de datos personales. Aplica cuando dejan de ser necesarios o si retiras consentimiento. Excepto si hay obligación legal de retener.</p>

          <h3>Derecho de oposición</h3>
          <p>Oponerte al tratamiento de tus datos, especialmente para fines de marketing directo.</p>

          <h3>Derecho a la portabilidad</h3>
          <p>Solicitar copia de tus datos en formato estructurado para transferir a otro responsable.</p>

          <h3>Cancelación de comunicaciones</h3>
          <p>Puedes cancelar el envío de comunicaciones comerciales en cualquier momento, incluyendo boletines, ofertas y actualizaciones de marketing.</p>

          <p>Para ejercer cualquiera de estos derechos, contáctanos con tu nombre completo, descripción clara del derecho que ejerces, datos que deseas consultar, modificar o eliminar, y acreditación de identidad si es necesario. Responderemos en máximo 30 días hábiles.</p>

          <hr />

          <h2>12. Enlaces a Terceros</h2>
          <p>Este sitio web podría contener enlaces a otros sitios que pudieran ser de tu interés. Una vez que hagas clic en estos enlaces y abandones nuestra página, ya no tenemos control sobre el sitio al que eres redirigido y por lo tanto no somos responsables de los términos o privacidad ni de la protección de tus datos en esos otros sitios terceros. Dichos sitios están sujetos a sus propias políticas de privacidad.</p>

          <hr />

          <h2>13. Menores de Edad</h2>
          <p>Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente datos de menores. Si detectamos que recopilamos datos de un menor sin consentimiento parental, los eliminaremos inmediatamente. Si eres menor y usas el Sitio, obtén autorización de un adulto responsable.</p>

          <hr />

          <h2>14. Control de tu Información Personal</h2>
          <p>Billora Law no venderá, cederá ni distribuirá la información personal que es recopilada sin tu consentimiento, salvo que sea requerido por un juez con una orden judicial.</p>
          <p>Cualquier cambio a esta Política de Privacidad será comunicado por email a usuarios registrados para cambios significativos, publicado en el Sitio con fecha de actualización, y requerirá nuevo consentimiento si son cambios materiales. Tu uso continuado del Sitio tras cambios implica aceptación de la Política actualizada.</p>

          <hr />

          <h2>15. Cambios a Esta Política</h2>
          <p>Billora Law se reserva el derecho de cambiar los términos de la presente Política de Privacidad en cualquier momento. Los cambios significativos serán notificados por email a usuarios registrados. Te recomendamos revisar esta política periódicamente para estar informado de cómo protegemos tus datos.</p>

          <hr />

          <h2>16. Contacto y Reclamos</h2>

          <h3>Para consultas sobre privacidad</h3>
          <p>Puedes contactarnos a través de nuestros canales oficiales disponibles en billoralaw.com.</p>

          <h3>Reclamo ante autoridades</h3>
          <p>Si consideras que tus derechos han sido vulnerados, puedes presentar reclamo ante:</p>
          <ul>
            <li>Consejo para la Transparencia (en materias de acceso a información)</li>
            <li>Fiscalía Nacional del Ministerio Público (si hay delito)</li>
            <li>Tribunal competente en tu jurisdicción</li>
          </ul>

          <h3>Mediación y escalado</h3>
          <p>Billora Law se compromete a resolver reclamos en forma colaborativa dentro de 30 días. Si no llegamos a acuerdo, la controversia puede derivarse a proceso de mediación voluntaria o proceso judicial según corresponda.</p>

          <hr />

          <h2>17. Legislación Aplicable</h2>
          <p>Esta Política de Privacidad se rige por las leyes de la República de Chile, en particular:</p>
          <ul>
            <li>Ley 19.628 sobre Protección de Datos Personales</li>
            <li>Ley 21.096 que moderniza protección de datos</li>
            <li>Ley 19.496 sobre Protección de Derechos del Consumidor</li>
            <li>Código del Trabajo (si aplica a relaciones laborales)</li>
          </ul>

          <hr />

          <h2>Nota Importante</h2>
          <p>Esta Política de Privacidad aplica exclusivamente al sitio web billoralaw.com. Si utilizas la plataforma SaaS de Billora Law (software de gestión de billing y casos para estudios jurídicos), aplica una Política de Privacidad adicional específica del software.</p>

          <hr />

          <p className="text-xs text-slate-400 mt-8">
            Esta política fue actualizada en mayo de 2026 en cumplimiento de buenas prácticas internacionales y regulación chilena vigente.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-100 mt-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-sm text-slate-400">
          <span>Billora Law - 2026</span>
          <Link to="/landing" className="hover:text-slate-600 transition-colors">Volver al inicio</Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
